export default function ServicesSection() {
  const services = [
    {
      icon: 'ri-user-star-line',
      title: 'Consulting / Fractional Head of AI',
      desc: 'Get Fortune 500-level AI leadership without the full-time executive cost. Peter embeds with your team to define AI strategy, build roadmaps, and drive execution — acting as your dedicated Head of AI on a flexible engagement model.',
      features: ['AI Strategy & Roadmap', 'Team Building & Mentorship', 'Vendor Evaluation', 'Executive Advisory'],
      cta: 'Book a Consultation',
      ctaLink: '#contact',
      highlight: true,
    },
    {
      icon: 'ri-code-s-slash-line',
      title: 'AI Project Work',
      desc: 'From concept to deployment, Peter and his team build custom AI solutions tailored to your specific business challenges — chatbots, voice agents, automation pipelines, and more.',
      features: ['Custom AI Development', 'Chatbots & Voice Agents', 'Process Automation', 'AI Integration'],
      cta: 'Start a Project',
      ctaLink: '#contact',
      highlight: false,
    },
    {
      icon: 'ri-apps-line',
      title: 'AI Products',
      desc: 'Ready-to-deploy AI products built by Peter\'s team that solve real business problems out of the box. Get enterprise-grade AI capabilities without the development overhead.',
      features: ['VoiceCal Booking System', 'AdSkipper for YouTube', 'Rapid Deployment', 'Ongoing Support'],
      cta: 'Explore Products',
      ctaLink: '#portfolio',
      highlight: false,
    },
  ];

  const aiProducts = [
    {
      logo: 'https://static.readdy.ai/image/b0d28b98390413eddff47342c2e73859/bee61946d7ab073c594d4bea26e8338c.png',
      name: 'VoiceCal Booking System',
      desc: 'An AI-powered voice booking system that lets customers schedule appointments naturally over the phone — no apps, no forms, just conversation.',
      links: [
        { label: 'Live Demo', href: 'https://jessica.petergits.com', icon: 'ri-external-link-line' },
      ],
      tags: ['Voice AI', 'Booking', 'NLP'],
    },
    {
      logo: null,
      name: 'AdSkipper for YouTube',
      desc: 'A browser extension that intelligently detects and skips YouTube ads using AI — giving users a seamless, uninterrupted viewing experience.',
      links: [
        { label: 'Watch Demo', href: 'https://youtube.com/shorts/9fcG8KLZv00', icon: 'ri-youtube-line' },
        { label: 'Beta Signup', href: 'https://staging.adskipper.petergits.com/beta-signup.html', icon: 'ri-user-add-line' },
      ],
      tags: ['Browser Extension', 'Ad Tech', 'AI'],
    },
  ];

  const scrollTo = (id: string) => {
    const el = document.getElementById(id.replace('#', ''));
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="py-24 px-6 lg:px-12 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-teal-50 text-teal-600 text-sm font-semibold rounded-full mb-4 tracking-wide uppercase">
            Services
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            How Our Team Can Help You
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Flexible AI engagement models designed to meet your business where it is and take it where it needs to go.
          </p>
          <div className="w-16 h-1 bg-teal-500 mx-auto rounded-full mt-6"></div>
        </div>

        {/* Service Cards */}
        <div className="grid lg:grid-cols-3 gap-8 mb-20">
          {services.map((s) => (
            <div
              key={s.title}
              className={`rounded-2xl p-8 flex flex-col border transition-all duration-200 hover:-translate-y-1 hover:shadow-xl ${
                s.highlight
                  ? 'bg-teal-600 text-white border-teal-500 shadow-lg shadow-teal-500/20'
                  : 'bg-white text-gray-900 border-gray-200 shadow-sm'
              }`}
            >
              <div className={`w-14 h-14 flex items-center justify-center rounded-xl mb-6 ${s.highlight ? 'bg-white/20' : 'bg-teal-50'}`}>
                <i className={`${s.icon} text-2xl ${s.highlight ? 'text-white' : 'text-teal-600'}`}></i>
              </div>
              <h3 className={`text-xl font-bold mb-3 ${s.highlight ? 'text-white' : 'text-gray-900'}`}>{s.title}</h3>
              <p className={`text-sm leading-relaxed mb-6 flex-1 ${s.highlight ? 'text-teal-100' : 'text-gray-500'}`}>{s.desc}</p>
              <ul className="space-y-2 mb-8">
                {s.features.map((f) => (
                  <li key={f} className={`flex items-center gap-2 text-sm ${s.highlight ? 'text-teal-100' : 'text-gray-600'}`}>
                    <i className={`ri-check-line flex-shrink-0 ${s.highlight ? 'text-white' : 'text-teal-500'}`}></i>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollTo(s.ctaLink)}
                className={`w-full py-3 rounded-xl font-semibold text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  s.highlight
                    ? 'bg-white text-teal-600 hover:bg-teal-50'
                    : 'bg-teal-600 text-white hover:bg-teal-700'
                }`}
              >
                {s.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Featured AI Products */}
        <div>
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-10">Featured AI Products</h3>
          <div className="grid lg:grid-cols-2 gap-8">
            {aiProducts.map((p) => (
              <div key={p.name} className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200">
                <div className="flex items-start gap-4 mb-4">
                  {p.logo ? (
                    <img src={p.logo} alt={p.name} className="w-14 h-14 object-contain rounded-xl border border-gray-100" />
                  ) : (
                    <div className="w-14 h-14 flex items-center justify-center bg-teal-50 rounded-xl border border-teal-100">
                      <i className="ri-youtube-line text-teal-600 text-2xl"></i>
                    </div>
                  )}
                  <div>
                    <h4 className="text-lg font-bold text-gray-900">{p.name}</h4>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {p.tags.map((t) => (
                        <span key={t} className="px-2 py-0.5 bg-teal-50 text-teal-600 text-xs rounded-full font-medium">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">{p.desc}</p>
                <div className="flex flex-wrap gap-3">
                  {p.links.map((l) => (
                    <a
                      key={l.label}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-teal-50 text-gray-700 hover:text-teal-600 text-sm font-medium rounded-lg border border-gray-200 hover:border-teal-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      <i className={`${l.icon} text-base`}></i>
                      {l.label}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
